import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { Divider, IconButton, Popper, Paper, MenuItem, ClickAwayListener, Typography } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import SettingsIcon from '@mui/icons-material/Settings';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import LogoutIcon from "@mui/icons-material/Logout";
import ScannerIcon from '@mui/icons-material/Scanner';
import BadgeIcon from '@mui/icons-material/Badge';
import TopicIcon from '@mui/icons-material/Topic';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

import SecurityIcon from '@mui/icons-material/Security';

import { useMediaQuery } from "@mui/material";
import { Link } from 'react-router-dom';
import UserContext from "../../context/user-context";
import httpRequest from "../../httpRequest";
import { useStyles } from "./styles";
import LinkItem from "./LinkItem/LinkItem";
import OCR_LOGO from "../../images/OCR-logo-black.png";
import { History } from "@mui/icons-material";

const SideMenu = (props) => {
  const classes = useStyles();
  const isSmallScreen = useMediaQuery("(max-width:540px)");
  const isExtraSmallScreen = useMediaQuery("(max-width:470px)");
  const [permissions, setPermissions] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState(null);
  const [selectedSubMenu, setSelectedSubMenu] = useState(null);
  const userCtx = useContext(UserContext); // Use UserContext instead

  // Destructure needed properties from userCtx
  const { employeeCode, overriddenRole, clearUserData } = userCtx;

  const arrowRef = useRef(null); // Create a ref for the arrow

  useEffect(() => {
    const fetchCurrentUserAndAccess = async () => {
      try {
        // Use employeeCode from userCtx directly
        const userOverrides = {
          canAccessScans: overriddenRole?.can_access_scans,
          canAddInvoice: overriddenRole?.can_add_invoice,
          canAddExpense: overriddenRole?.can_add_expense,
          canAccessExpense: overriddenRole?.can_access_expense,
          canGeneralScan: overriddenRole?.can_general_scan,
          canSettings: overriddenRole?.can_settings,
          canConfigurator: overriddenRole?.can_configurator,
          canUserManagement: overriddenRole?.can_user_management,
          canVendor: overriddenRole?.can_vendor,
          canDocumentScreen: overriddenRole?.can_add_documents || overriddenRole?.can_manage_documents,
          hasExpenseInboxAccess: overriddenRole?.expense_inbox_access,
          hasInvoiceInboxAccess: overriddenRole?.invoice_inbox_access
        };
        setPermissions(userOverrides);

        if (employeeCode === "admin") {
          setIsAdmin(true);
          return; 
        }
        
      } catch (error) {
        console.error("Can't fetch permissions", error);
      }
    };

    fetchCurrentUserAndAccess();
  }, [employeeCode, overriddenRole]);
  
  const handleSettingsClick = useCallback((event) => {
    setSubMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseSubMenu = useCallback(() => {
    setSubMenuAnchorEl(null);
    setSelectedSubMenu(null); // Reset the selected submenu when closing
  }, []);

  const handleSubMenuClick = (subMenu) => {
    setSelectedSubMenu(subMenu === selectedSubMenu ? null : subMenu);
  };


  const logoutUser = async () => {
    await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/logout`);
    clearUserData();
    window.location.href = "/login";
  };

  return (
    <div
      className={`${classes.rootContainer} ${
        props.visible ? "" : classes.rootContainerCollapsed
      }`}
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {props.visible && (
        <>
          <div>
            <div className={classes.closeContainer}>
              <IconButton onClick={props.onClose} className={classes.closeButton}>
                <ArrowBackIosIcon
                  sx={{
                    color: "#19334e",
                    "&:hover": {
                      color: "#39a3e3",
                    },
                  }}
                />
              </IconButton>
            </div>
            <Link to="/">
              <img
                src={OCR_LOGO}
                alt="logo"
                className={classes.logo}
                width={isSmallScreen ? "40px" : "140px"}
                style={{
                  marginTop: isExtraSmallScreen ? "5px" : "-16px",
                  marginBottom: "24px",
                }}
              />
            </Link>
            <Divider />
            <div className={classes.linkContainer}>
              <LinkItem to="/" Icon={DashboardIcon} text="Dashboard" />
              {props.activeTab === 0 && !isAdmin && (
                <>
                  {(permissions.canAddInvoice || permissions.hasInvoiceInboxAccess) && (
                  <LinkItem to="/my-invoices" Icon={RecentActorsIcon} text="My Invoices" />
                  )}

                  {permissions.canAddInvoice && <LinkItem to="/scan" Icon={AddAPhotoIcon} text="Add Invoice" />}
                  {permissions.canAccessScans && <LinkItem to="/history" Icon={ChecklistRtlIcon} text="All Scans" />}
                </>
              )}
              {props.activeTab === 2 && !isAdmin && (
                <>
                  {permissions.canAddExpense && (
                    <>
                      <LinkItem to="/add-expenses" Icon={AddAPhotoIcon} text="Add Expenses" />
                      <LinkItem to="/my-expenses" Icon={MonetizationOnIcon} text="My Expenses" />
                    </>
                  )}
                  {permissions.hasExpenseInboxAccess && (
                    <>
                    <LinkItem to="/expense-reimbursements" Icon={BadgeIcon} text="Expense Inbox" />
                    <LinkItem to="/expense-reimbursements-history" Icon={History} text="Inbox History" />
</>
                  )}
                </>
              )}

              {permissions.canGeneralScan && <> {/*<LinkItem to="/general-scan" Icon={ScannerIcon} text="General Scan" /> */}
              <LinkItem to="/doc-extraction" Icon={ScannerIcon} text="Doc Extractor" /></>}
              <LinkItem to="/merger" Icon={ScannerIcon} text="Merger" />
              <LinkItem to="/ocr-scanner" Icon={ScannerIcon} text="OCR Scanner" />
             
              {permissions.canSettings && (
                <div>
                  <Link to="" className={classes.link}>
                    <Typography variant="h7" sx={{ fontFamily: "SapFiori72, sans-serif" }}>
                      <SettingsIcon className={classes.linkIcon} />
                      <span>Settings</span>
                    </Typography>
                  </Link>
                  <IconButton onClick={handleSettingsClick}>
                    <ChevronRightIcon style={{ color: "#19334e" }} />
                  </IconButton>
                  
                  {Boolean(subMenuAnchorEl) && (
                    <ClickAwayListener onClickAway={handleCloseSubMenu}>
                      
                      <Popper
                        open={Boolean(subMenuAnchorEl)}
                        anchorEl={subMenuAnchorEl}
                        placement="right-start"
                        disablePortal={false}
                        style={{ zIndex: 999 }}
                        modifiers={[
                          {
                            name: 'offset',
                            options: {
                              offset: [0, 40],
                            },
                          },
                          
                        ]}
                      >
                          
                       <Paper
          style={{
            color: "#19334e",
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
            border: '1px solid #19334e',
          }}
        >
         
         {isAdmin && <MenuItem onClick={() => handleSubMenuClick('organization')}>
                            <TopicIcon sx={{ marginRight: '8px' }} />
                            Organization Structure
                          </MenuItem>}
                          
                          {isAdmin && <MenuItem onClick={() => handleSubMenuClick('humanCapital')}>
                            <TopicIcon sx={{ marginRight: '8px' }} />
                            Human Capital
                          </MenuItem>}
                          
                          {permissions.canVendor && <MenuItem onClick={() => handleSubMenuClick('vendor')}>
                            <TopicIcon sx={{ marginRight: '8px' }} />
                            Vendor
                          </MenuItem>}
                          
                          {isAdmin && <MenuItem onClick={() => handleSubMenuClick('customer')}>
                            <TopicIcon sx={{ marginRight: '8px' }} />
                            Customer
                          </MenuItem>}
                          
                          {isAdmin && <MenuItem onClick={() => handleSubMenuClick('equipment')}>
                            <TopicIcon sx={{ marginRight: '8px' }} />
                            Equipment
                          </MenuItem>}

                          
                          
                          {permissions.canUserManagement && <MenuItem onClick={() => handleSubMenuClick('security')}>
                            <SecurityIcon sx={{ marginRight: '8px' }} />
                            Security
                          </MenuItem>}
                         
                          {permissions.canConfigurator && <MenuItem onClick={() => handleSubMenuClick('environment')}>
                            <TopicIcon sx={{ marginRight: '8px' }} />
                            Environment Setup
                          </MenuItem>}
                         
                          
                        </Paper>
                      </Popper>
                    </ClickAwayListener>
                  )}

{selectedSubMenu && (
              <ClickAwayListener onClickAway={() => setSelectedSubMenu(null)}>
                <Popper
                  open={Boolean(selectedSubMenu)}
                  anchorEl={subMenuAnchorEl}
                  placement="right-start"
                  disablePortal={false}
                  style={{ zIndex: 999 }}
                  modifiers={[
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 278],
                      },
                    },
                  ]}
                >
                  <Paper style={{ color: "#19334e", background: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(10px)', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)', border: '1px solid #19334e' }}>
                    {/* Render submenu items based on selectedSubMenu */}
                    {selectedSubMenu === 'organization' && (
                      <div>
                        <MenuItem component={Link} to="/settings?tab=1">Company</MenuItem>
                      </div>
                    )}
                    {selectedSubMenu === 'humanCapital' && (
                      <div>
                        <MenuItem component={Link} to="/settings?tab=2">Function</MenuItem>
                        <MenuItem component={Link} to="/settings?tab=5">Position</MenuItem>
                        <MenuItem component={Link} to="/settings?tab=6">Employee</MenuItem>
                      </div>
                    )}
                    {selectedSubMenu === 'vendor' && (
                      <div>
                        <MenuItem component={Link} to="/vendors">Vendor Master</MenuItem>
                        <MenuItem component={Link} to="/settings">Tax Codes</MenuItem>
                      </div>
                    )}
                    {selectedSubMenu === 'customer' && (
                      <div>
                        <MenuItem component={Link} to="/settings">Customer Master</MenuItem>
                        <MenuItem component={Link} to="/settings">Tax Codes</MenuItem>
                      </div>
                    )}
                    {selectedSubMenu === 'equipment' && (
                      <div>
                        <MenuItem component={Link} to="/settings">Equipment Master</MenuItem>
                      </div>
                    )}
                    
                    {selectedSubMenu === 'security' && (
                      <div>
                        <MenuItem component={Link} to="/users">User Setup</MenuItem>
                        <MenuItem component={Link} to="/settings?tab=3">Authorization</MenuItem>
                        {isAdmin &&  <MenuItem component={Link} to="/audits">User Logs</MenuItem> }
                      </div>
                    )}
                    {selectedSubMenu === 'environment' && (
                      <div>
                        <MenuItem component={Link} to="/settings?tab=8">Approval Workflow</MenuItem>
                        {isAdmin && <MenuItem component={Link} to="/settings">ERP Integration</MenuItem>}
                        <MenuItem component={Link} to="/settings?tab=0">Configurator</MenuItem>
                        <MenuItem component={Link} to="/settings?tab=4">Mailbox Setup</MenuItem>
                        {isAdmin && <MenuItem component={Link} to="/settings">Whatsapp Integration</MenuItem>}
                        <MenuItem component={Link} to="/settings?tab=7">Expense Categories</MenuItem>
                        <MenuItem component={Link} to="/settings?tab=9">DMS Integration</MenuItem>
                      </div>
                    )}
                  </Paper>
                </Popper>
              </ClickAwayListener>
            )}
                </div>
              )}
            </div>
          </div>
          <div className={classes.footerContainer}>
            <IconButton onClick={logoutUser}>
              <LogoutIcon
                sx={{
                  color: "#19334e",
                  fontSize: 30,
                  "&:hover": {
                    color: "#39a3e3",
                  },
                }}
              />
            </IconButton>
          </div>
        </>
      )}
    </div>
  );
};

export default SideMenu;
